import { OperationEntity } from '@/types/Operation';
import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Watch, Prop } from '@Ravnur-Inc/decorators';
import { showErrorNotification } from '@Ravnur-Inc/notifications/service';
import { Vue, Options } from 'vue-class-component';

import LiveSplashImage from './live-splash-image';
import humanFileSize from '@Ravnur-Inc/helpers/human-file-size';

import './live-splash-uploader.scss';

export const MAX_SPLASH_SIZE = 500 * 1000;

const CN = 'live-splash-uploader';

type Props = {
  label: string;
  src: Nullable<string>;
  fallback?: Nullable<string>;
  uploadMethod: UploadMethod;
  removeMethod: RemoveMethod;
  entityId?: Nullable<string>;
};

type Emits = {
  onChange: (hasSplash: boolean) => void;
};

type UploadMethod = (file: File, id: string) => Promise<OperationEntity<unknown>>;
type RemoveMethod = (id: string) => Promise<OperationEntity<unknown>>;

@Options({
  emits: ['change'],
})
export default class LiveSplashUploader extends Vue {
  declare $props: $Types<Props, Emits>;
  @Prop({ type: Function, required: true }) public uploadMethod!: UploadMethod;
  @Prop({ type: Function, required: true }) public removeMethod!: RemoveMethod;
  @Prop({ type: String, default: null }) public entityId: Nullable<string>;
  @Prop({ type: String, default: null }) public src!: Nullable<string>;
  @Prop({ type: Number, default: null }) public maxSize!: Nullable<number>;
  @Prop({ type: String, default: null }) public fallback!: Nullable<string>;
  @Prop({ type: String, required: true }) public label!: string;
  @Prop({ type: String, default: true }) public hasInfoMessage: boolean;
  @Prop({ type: String, default: 'image/gif,image/jpeg,image/png' })
  public acceptedFormats: boolean;

  public file: Nullable<File> = null;
  private unploadedSrc: Nullable<string> = null;
  private cleared = false;

  private get hasImage() {
    return !this.cleared && (this.unploadedSrc || this.source);
  }

  private get source() {
    return this.cleared ? this.fallback : this.unploadedSrc || this.src || this.fallback;
  }

  public async pushSplash(id?: string) {
    const { file, uploadMethod, removeMethod, entityId, cleared, src } = this;
    const finalId = id ? id : entityId;

    if (!finalId) {
      return;
    }

    if (file) {
      await uploadMethod(file, finalId);
    } else if (src && cleared) {
      await removeMethod(finalId);
    }
  }

  @Watch('src', { immediate: true })
  protected handleSourcesChanged() {
    this.cleared = false;
    this.file = null;
    this.unploadedSrc = null;
  }

  render() {
    const { label } = this;

    return (
      <div class={CN}>
        <span class={`${CN}__label`}>{label}</span>
        <l10n group="live-channels" tkey="page__splash-info" v-show={this.hasInfoMessage} />

        <div class={`${CN}__uploader-wrapper`}>
          <LiveSplashImage
            class={`${CN}__splash`}
            onerror={this.handleImageError}
            src={this.source || undefined}
          />

          <div class={`${CN}__img-actions`}>
            <file-button
              accept={this.acceptedFormats}
              class={[`${CN}__btn`, !this.hasImage ? `${CN}__btn--single` : '']}
              color="primary"
              mode="borderless"
              onFileSelected={this.selectSplash}
            >
              {this.hasImage ? (
                <l10n group="common" tkey="action__change" />
              ) : (
                <span class={`${CN}__btn-content-no-image`}>
                  <icon type="camera" />
                  <l10n group="common" tkey="action__upload" />
                </span>
              )}
            </file-button>

            {this.hasImage ? (
              <r-button
                class={`${CN}__btn`}
                color="primary"
                mode="borderless"
                onclick={this.removeSplash}
              >
                <l10n group="live-channels" tkey="action__delete" />
              </r-button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  private selectSplash(file: File): Promise<void> | void {
    this.file = file;

    if (this.maxSize) {
      const size = humanFileSize(this.maxSize);

      if (file.size > this.maxSize) {
        showErrorNotification(this.$t('common', 'upload__file-size', { size }));

        return;
      }
    }

    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onloadend = () => {
        this.cleared = false;
        this.unploadedSrc = reader.result as string;
        resolve();
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
      this.$emit('change', true);
    });
  }

  private handleImageError() {
    this.unploadedSrc = null;
    this.file = null;
    this.$emit('change', true);
    showErrorNotification(this.$t('live-channels', 'file__not-image'));
  }

  public removeSplash() {
    this.file = null;
    this.cleared = true;
    this.unploadedSrc = null;

    this.$emit('change', false);
  }
}
